import { useCallback, useContext, useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import { inputStyles } from '@/styles/input';
import { CheckoutContext } from '@/context/CheckoutContext';
import { SkeletonLoader } from '../Common/SkeletonLoader';
import { Input } from '../Common/Input';
import { Email } from '../Common/Email';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { useAppchargeCard } from '@/hooks/useAppchargeCard';
import { type FieldMetadata } from '@/utils/appchargePaymentElement';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useTranslate } from '@/hooks/useTranslate';
import { shouldHideEmail } from '@/utils/conditionalRendering';
import { isEmailValid } from '@/utils/email';
import { ValidationRules } from '@/utils/validation';
import { FeatureFlag } from '@/constants/featureFlags';
import { trackFormEntryCompleted } from '@/utils/checkoutMixpanelTracking';
import { sendFormEntryCompleted } from '@/utils/checkoutUiInteractionsTracking';
import { useLatestRef } from '@/hooks/useLatestRef';

export const AppchargePaymentElementForm = () => {
  const context = useContext(CheckoutContext);
  const {
    customer,
    featureFlags,
    template,
    actions: { setIsFormValid },
    card: { initialCardPayload, setInitialCardPayload },
  } = context;
  const { t } = useTranslate();
  const contextRef = useLatestRef(context);

  // Mirrors the Adyen card form: the cardholder-name field is only shown (and
  // only enforced) when the publisher opts in via the Adyen cardholder flag.
  // When it's off the field is hidden and the BE-optional empty name is sent.
  const showCardholderName = Boolean(featureFlags?.[FeatureFlag.CHECKOUT_SHOW_ADYEN_CARDHOLDER_NAME_FIELD]);

  const { isLoading, isCardFormValid, cardNumberMetadata, expiryMetadata, cvvMetadata } = useAppchargeCard();

  const [isZipCodeValid, setIsZipCodeValid] = useState(false);

  const {
    values,
    errors,
    touched,
    setValue,
    setFieldTouched,
    isValid: isCardholderNameValid,
  } = useFormValidation(
    { cardholderName: initialCardPayload?.cardholderName ?? '' },
    {
      cardholderName: [
        ValidationRules.required(t('cardholderName'), t('fieldErrors.incomplete', { field: t('cardholderName') })),
        ValidationRules.cardholderName(t('fieldErrors.incompleteCardholderName')),
      ],
    }
  );

  const isPayFormValid =
    isCardFormValid &&
    !isLoading &&
    isZipCodeValid &&
    (!showCardholderName || isCardholderNameValid) &&
    isEmailValid(customer?.email);

  useEffect(() => {
    setIsFormValid(isPayFormValid);
  }, [
    isCardFormValid,
    isLoading,
    isZipCodeValid,
    showCardholderName,
    isCardholderNameValid,
    customer?.email,
    setIsFormValid,
  ]);

  useEffect(() => {
    if (isPayFormValid) {
      trackFormEntryCompleted(contextRef.current);
      sendFormEntryCompleted(contextRef.current);
    }
  }, [isPayFormValid]);

  const handleTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  const handleCardholderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue('cardholderName', value);
    setInitialCardPayload({ ...initialCardPayload, cardholderName: value });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-4 w-full relative">
        {isLoading && <AppchargePaymentElementFormSkeleton />}

        <div className={cn('flex flex-col gap-2', isLoading && 'invisible')}>
          <div className="flex flex-col gap-1">
            <ACCardElementField metadata={cardNumberMetadata} />
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex flex-col gap-1 flex-1">
              <ACCardElementField metadata={expiryMetadata} />
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <ACCardElementField metadata={cvvMetadata} />
            </div>
          </div>
        </div>
      </div>

      {showCardholderName && (
        <Input
          type="text"
          name="holderName"
          autoComplete="cc-name"
          placeholder={t('cardholderName')}
          value={values.cardholderName}
          onChange={handleCardholderNameChange}
          onBlur={() => setFieldTouched('cardholderName')}
          error={touched.cardholderName ? errors.cardholderName : undefined}
          className="w-full h-11 text-base"
        />
      )}

      <InputTaxComponent onValidate={handleTaxValidationChange} />
      {!shouldHideEmail(featureFlags, customer, template.features) && (
        <div className="w-full">
          <Email />
        </div>
      )}
    </div>
  );
};

interface AppchargePaymentElementFieldProps {
  metadata: FieldMetadata;
}

export function ACCardElementField({ metadata }: AppchargePaymentElementFieldProps) {
  return (
    <>
      <div
        ref={metadata.ref}
        className={cn(
          inputStyles,
          'w-full h-11 p-0 overflow-hidden',
          metadata.isFocused && 'ac-field--focused',
          metadata.error && 'border-danger',
          !metadata.error && metadata.isFocused && 'border-muted-foreground active:border-muted-foreground'
        )}
      />
      {metadata.error && <div className="text-xs text-red-500">{metadata.error}</div>}
    </>
  );
}

function AppchargePaymentElementFormSkeleton() {
  return (
    <div className="flex flex-col gap-4 w-full absolute inset-0 z-10">
      <SkeletonLoader height="h-11" width="w-full" />
      <div className="flex gap-4 w-full">
        <SkeletonLoader height="h-11" width="w-1/2" />
        <SkeletonLoader height="h-11" width="w-1/2" />
      </div>
    </div>
  );
}
